import { FilmInventory } from "@/types/inventory";
import { FilmStatusBadge } from "./FilmStatusBadge";
import { FilmActions } from "./FilmActions";
import { useNavigate } from "react-router-dom";
import { format } from "date-fns";
import { useIsMobile } from "@/hooks/use-mobile";
import { useTranslation } from "@/hooks/useTranslation";

interface FilmCardProps {
  film: FilmInventory;
  showTransfer?: boolean;
  onActionComplete: () => void;
}

export function FilmCard({ film, showTransfer = false, onActionComplete }: FilmCardProps) {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const { t } = useTranslation();

  const formatExpirationDate = (dateString: string | null) => {
    if (!dateString) return t('not_applicable');
    try {
      return format(new Date(dateString), 'MMMM yyyy');
    } catch {
      return t('invalid_date');
    }
  };

  // Return compact card design for mobile
  if (isMobile) {
    return (
      <div 
        className="p-4 border rounded-lg bg-card hover:bg-accent/50 cursor-pointer transition-colors shadow-sm"
        onClick={() => navigate(`/inventory/${film.id}`)}
      >
        <div className="flex justify-between items-center">
          <div className="min-w-0 flex-1">
            <h3 className="font-medium text-base truncate">{film.brand}</h3>
            <p className="text-sm text-muted-foreground truncate">{film.film_name}</p>
          </div>
          <FilmStatusBadge status={film.status} />
        </div>
        
        <div className="flex justify-between items-center mt-3">
          <div className="flex space-x-4 text-sm">
            <div>
              <span className="text-muted-foreground">{t('qty')}: </span>
              <span>{film.quantity}</span>
            </div>
            <div>
              <span className="text-muted-foreground">{t('type')}: </span>
              <span>{film.film_type}</span>
            </div>
          </div>
          
          <div onClick={(e) => e.stopPropagation()} className="ml-2">
            <FilmActions 
              film={film} 
              showTransfer={showTransfer} 
              onActionComplete={onActionComplete}
            />
          </div>
        </div>
      </div>
    );
  }

  // Return standard card for desktop view
  return (
    <div 
      className="p-4 border rounded-lg space-y-3 bg-card hover:bg-accent/50 cursor-pointer transition-colors"
      onClick={() => navigate(`/inventory/${film.id}`)}
    >
      <div className="flex justify-between items-start gap-2">
        <div className="min-w-0">
          <h3 className="font-medium text-sm truncate">{film.brand}</h3>
          <p className="text-xs text-muted-foreground truncate">{film.film_name}</p>
        </div>
        <FilmStatusBadge status={film.status} />
      </div>
      
      <div className="grid grid-cols-2 gap-3 text-sm">
        <div>
          <p className="text-muted-foreground text-xs">Type</p>
          <p>{film.film_type}</p>
        </div>
        <div>
          <p className="text-muted-foreground text-xs">ISO</p>
          <p>{film.iso}</p>
        </div>
        <div>
          <p className="text-muted-foreground text-xs">Quantity</p>
          <p>{film.quantity}</p>
        </div>
        <div>
          <p className="text-muted-foreground text-xs">Expires</p>
          <p>{formatExpirationDate(film.expiration_date)}</p>
        </div>
      </div>

      {film.notes && (
        <div className="pt-2 border-t">
          <p className="text-xs text-muted-foreground mb-1">Notes</p>
          <p className="text-sm">{film.notes}</p>
        </div>
      )}

      <div className="flex justify-end pt-2" onClick={(e) => e.stopPropagation()}>
        <FilmActions 
          film={film} 
          showTransfer={showTransfer} 
          onActionComplete={onActionComplete}
        />
      </div>
    </div>
  );
}
